import React, { useState } from 'react';
import { DollarSign, TrendingUp, AlertCircle, CheckCircle, Plus, Download, X, Lock, Edit2, Trash2, FileText } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { generateFinanceReportPdf, generateRecordPdf, sendInvoiceToClient, sendQuotationToClient } from '../../services/backendActions';
import './AdminPages.css';

const INV_BADGE = { Paid:'badge-success', Pending:'badge-warning', Overdue:'badge-danger' };
const QUO_BADGE = { Accepted:'badge-success', Pending:'badge-warning', Draft:'badge-gray', Declined:'badge-danger', 'Pending Approval':'badge-warning', Approved:'badge-success', Sent:'badge-info' };

export default function FinancePage() {
  const { data, commitData, commitMany, showToast } = useApp();
  const { can, isFounder, user } = useAuth();

  const canView   = can('canViewFinance');
  const canCreate = can('canCreateInvoice');
  const canExport = can('canExportReports');

  if (!canView) {
    return (
      <div className="admin-page">
        <div className="page-header"><div><h1 className="page-title">Finance</h1></div></div>
        <div className="perm-denied">
          <div className="perm-denied-icon"><Lock size={26} style={{ color:'var(--text-dim)' }}/></div>
          <p style={{ fontWeight:700 }}>Access Restricted</p>
          <p style={{ color:'var(--text-muted)',fontSize:'0.875rem' }}>Finance is only available to Finance Managers, Administrators, and Founders.</p>
        </div>
      </div>
    );
  }

  const [tab, setTab]           = useState('invoices');
  const [showInvModal, setShowInvModal] = useState(false);
  const [showQuoModal, setShowQuoModal] = useState(false);
  const [editInv, setEditInv]   = useState(null);
  const [editQuo, setEditQuo]   = useState(null);
  const [invForm, setInvForm]   = useState({ client:'', amount:'', due:'', service:'', status:'Pending' });
  const [quoForm, setQuoForm]   = useState({ client:'', amount:'', service:'', status:'Draft' });

  const paid    = data.invoices.filter(i=>i.status==='Paid').reduce((s,i)=>s+i.amount,0);
  const pending = data.invoices.filter(i=>i.status==='Pending').reduce((s,i)=>s+i.amount,0);
  const overdue = data.invoices.filter(i=>i.status==='Overdue').reduce((s,i)=>s+i.amount,0);

  const upsertApprovalRequest = (requests, payload) => {
    const idx = requests.findIndex((r) => r.ref === payload.ref);
    if (idx >= 0) {
      return requests.map((r, i) => (i === idx ? { ...r, ...payload } : r));
    }
    return [...requests, { id: String(Date.now()), ...payload }];
  };

  /* ── Invoice CRUD ── */
  const saveInvoice = async () => {
    if (!invForm.client || !invForm.amount) { showToast('Client and amount required','error'); return; }
    try {
      if (editInv) {
        await commitData('invoices', data.invoices.map(i=>i.id===editInv.id?{...i,...invForm,amount:parseFloat(invForm.amount)}:i));
        showToast('Invoice updated');
      } else {
        const id = `INV-${String(data.invoices.length+1).padStart(3,'0')}`;
        await commitMany({
          invoices: [...data.invoices,{ id, ...invForm, amount:parseFloat(invForm.amount), date:new Date().toISOString().split('T')[0], approvals:0 }],
          approvalRequests: upsertApprovalRequest(data.approvalRequests || [], {
            type: 'Invoice',
            ref: id,
            requestedBy: user?.employeeId != null ? String(user.employeeId) : '',
            requestedByUid: user?.uid || '',
            requestedByName: user?.name || 'Finance',
            amount: parseFloat(invForm.amount),
            client: invForm.client,
            date: new Date().toISOString().split('T')[0],
            approvedByUids: [],
            approvedBy: [],
            status: 'Pending',
          }),
        });
        showToast('Invoice submitted — founders notified by email when mail is configured');
      }
      setShowInvModal(false); setEditInv(null); setInvForm({ client:'',amount:'',due:'',service:'',status:'Pending' });
    } catch { /* commitData already toasts */ }
  };

  const openEditInv = (inv) => { setEditInv(inv); setInvForm({ client:inv.client,amount:String(inv.amount),due:inv.due,service:inv.service||'',status:inv.status }); setShowInvModal(true); };
  const deleteInv   = async (id) => { if(!window.confirm('Delete invoice?'))return; try { await commitData('invoices',data.invoices.filter(i=>i.id!==id)); showToast('Invoice deleted','warning'); } catch {} };

  const markPaid = async (id) => {
    try {
      await commitData('invoices', data.invoices.map(i=>i.id===id?{...i,status:'Paid'}:i));
      showToast('Invoice marked as paid');
    } catch {}
  };

  /* ── Quotation CRUD ── */
  const saveQuo = async () => {
    if (!quoForm.client || !quoForm.amount) { showToast('Client and amount required','error'); return; }
    const quots = data.quotations || [];
    try {
      if (editQuo) {
        await commitData('quotations', quots.map(q=>q.id===editQuo.id?{...q,...quoForm,amount:parseFloat(quoForm.amount)}:q));
        showToast('Quotation updated');
      } else {
        const id = `QUO-${String(quots.length+1).padStart(3,'0')}`;
        await commitMany({
          quotations: [...quots,{ id,...quoForm,amount:parseFloat(quoForm.amount),date:new Date().toISOString().split('T')[0],approvals:0,status:'Pending Approval',sentToClient:false }],
          approvalRequests: upsertApprovalRequest(data.approvalRequests || [], {
            type: 'Quotation',
            ref: id,
            requestedBy: user?.employeeId != null ? String(user.employeeId) : '',
            requestedByUid: user?.uid || '',
            requestedByName: user?.name || 'Finance',
            amount: parseFloat(quoForm.amount),
            client: quoForm.client,
            date: new Date().toISOString().split('T')[0],
            approvedByUids: [],
            approvedBy: [],
            status: 'Pending',
          }),
        });
        showToast('Quotation submitted — founders notified by email when mail is configured');
      }
      setShowQuoModal(false); setEditQuo(null); setQuoForm({ client:'',amount:'',service:'',status:'Draft' });
    } catch {}
  };

  const deleteQuo = async (id) => { if(!window.confirm('Delete quotation?'))return; try { await commitData('quotations',(data.quotations||[]).filter(q=>q.id!==id)); showToast('Quotation deleted','warning'); } catch {} };

  const exportReport = async () => {
    try {
      await generateFinanceReportPdf();
      showToast('Finance report PDF downloaded');
    } catch (err) {
      showToast(err.message || 'Could not export report', 'error');
    }
  };

  const downloadRecordPdf = async (type, id) => {
    try {
      await generateRecordPdf(type, id);
      showToast(`${type === 'invoice' ? 'Invoice' : 'Quotation'} PDF downloaded`);
    } catch (err) {
      showToast(err.message || 'Could not generate PDF', 'error');
    }
  };

  const sendQuoteNow = async (q) => {
    try {
      await sendQuotationToClient(q.id);
      await commitData('quotations', (data.quotations || []).map((item) => item.id === q.id ? { ...item, status:'Sent', sentToClient:true } : item));
      showToast(`Quotation ${q.id} sent to client`);
    } catch (err) {
      showToast(err.message || 'Could not send quotation', 'error');
    }
  };

  const sendInvoiceNow = async (inv) => {
    try {
      await sendInvoiceToClient(inv.id);
      await commitData('invoices', data.invoices.map((item) => item.id === inv.id ? { ...item, sentToClient: true } : item));
      showToast(`Invoice ${inv.id} sent to client`);
    } catch (err) {
      showToast(err.message || 'Could not send invoice', 'error');
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <div><h1 className="page-title">Finance</h1><p className="page-subtitle">Track revenue, invoices and quotations</p></div>
        <div className="page-actions">
          {canExport && <button className="btn-ghost" onClick={exportReport}><Download size={15}/> Export Report</button>}
          {canCreate && <button className="btn-outline" onClick={()=>{setEditQuo(null);setQuoForm({client:'',amount:'',service:'',status:'Draft'});setShowQuoModal(true)}}><Plus size={15}/> New Quotation</button>}
          {canCreate && <button className="btn-gold"    onClick={()=>{setEditInv(null);setInvForm({client:'',amount:'',due:'',service:'',status:'Pending'});setShowInvModal(true)}}><Plus size={15}/> New Invoice</button>}
        </div>
      </div>

      {/* Summary stats */}
      <div className="stats-row">
        {[
          { icon:DollarSign, value:`R${(paid/1000).toFixed(0)}k`,    label:'Collected',   color:'var(--success)' },
          { icon:TrendingUp, value:`R${(pending/1000).toFixed(0)}k`, label:'Outstanding', color:'var(--warning)' },
          { icon:AlertCircle,value:`R${(overdue/1000).toFixed(0)}k`, label:'Overdue',     color:'var(--danger)' },
          { icon:FileText,   value:data.invoices.length,             label:'Invoices',    color:'var(--secondary-color)' },
          { icon:FileText,   value:(data.quotations||[]).length,     label:'Quotations',  color:'var(--info)' },
        ].map(s=>(
          <div key={s.label} className="stat-card">
            <div className="stat-top"><div className="stat-icon" style={{ background:`${s.color}15`,color:s.color }}><s.icon size={18}/></div></div>
            <div className="stat-value" style={{ color:s.color }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="filter-tabs" style={{ marginBottom:20,width:'fit-content' }}>
        <button className={`filter-tab ${tab==='invoices'?'active':''}`} onClick={()=>setTab('invoices')}>Invoices</button>
        <button className={`filter-tab ${tab==='quotations'?'active':''}`} onClick={()=>setTab('quotations')}>Quotations</button>
      </div>

      {/* ── INVOICES ── */}
      {tab==='invoices' && (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Invoice ID</th><th>Client</th><th>Service</th><th>Amount</th><th>Issue Date</th><th>Due</th><th>Approvals</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {data.invoices.map(inv=>(
                <tr key={inv.id}>
                  <td><span style={{ fontFamily:'var(--font-mono)',color:'var(--secondary-color)',fontWeight:700 }}>{inv.id}</span></td>
                  <td style={{ fontWeight:600 }}>{inv.client}</td>
                  <td style={{ fontSize:'0.8rem',color:'var(--text-muted)',maxWidth:180 }}>{inv.service}</td>
                  <td><span style={{ fontFamily:'var(--font-mono)',fontWeight:700 }}>R {inv.amount.toLocaleString()}</span></td>
                  <td style={{ fontSize:'0.8rem',color:'var(--text-muted)' }}>{new Date(inv.date).toLocaleDateString('en-ZA')}</td>
                  <td style={{ fontSize:'0.8rem',color:'var(--text-muted)' }}>{inv.due ? new Date(inv.due).toLocaleDateString('en-ZA') : '—'}</td>
                  <td>
                    <div style={{ display:'flex',gap:4,alignItems:'center' }}>
                      {[0,1].map(i=><div key={i} className={`approval-dot ${i<(inv.approvals||0)?'filled':'empty'}`}>{i<(inv.approvals||0)?'✓':i+1}</div>)}
                    </div>
                  </td>
                  <td><span className={`badge ${INV_BADGE[inv.status]||'badge-gray'}`}>{inv.status}</span></td>
                  <td>
                    <div style={{ display:'flex',gap:5 }}>
                      {inv.status==='Pending' && isFounder && <button className="btn-gold" style={{ padding:'4px 8px',fontSize:'0.7rem' }} onClick={()=>markPaid(inv.id)}>Mark Paid</button>}
                      <button className="btn-outline" style={{ padding:'4px 8px',fontSize:'0.7rem' }} disabled={(inv.approvals||0) < 2 || inv.sentToClient} onClick={()=>sendInvoiceNow(inv)}>Send</button>
                      <button className="btn-ghost" style={{ padding:'4px 8px',fontSize:'0.7rem' }} onClick={()=>downloadRecordPdf('invoice', inv.id)}><Download size={11}/></button>
                      {canCreate && <button className="btn-ghost" style={{ padding:'4px 8px',fontSize:'0.7rem' }} onClick={()=>openEditInv(inv)}><Edit2 size={11}/></button>}
                      {isFounder && <button className="btn-ghost" style={{ padding:'4px 8px',fontSize:'0.7rem',color:'var(--danger)' }} onClick={()=>deleteInv(inv.id)}><Trash2 size={11}/></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── QUOTATIONS ── */}
      {tab==='quotations' && (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>QUO ID</th><th>Client</th><th>Service</th><th>Amount</th><th>Date</th><th>Approvals</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {(data.quotations||[]).map(q=>(
                <tr key={q.id}>
                  <td><span style={{ fontFamily:'var(--font-mono)',color:'var(--secondary-color)',fontWeight:700 }}>{q.id}</span></td>
                  <td style={{ fontWeight:600 }}>{q.client}</td>
                  <td style={{ fontSize:'0.8rem',color:'var(--text-muted)' }}>{q.service}</td>
                  <td><span style={{ fontFamily:'var(--font-mono)',fontWeight:700 }}>R {q.amount.toLocaleString()}</span></td>
                  <td style={{ fontSize:'0.8rem',color:'var(--text-muted)' }}>{new Date(q.date).toLocaleDateString('en-ZA')}</td>
                  <td>
                    <div style={{ display:'flex',gap:4 }}>
                      {[0,1].map(i=><div key={i} className={`approval-dot ${i<(q.approvals||0)?'filled':'empty'}`}>{i<(q.approvals||0)?'✓':i+1}</div>)}
                    </div>
                  </td>
                  <td><span className={`badge ${QUO_BADGE[q.status]||'badge-gray'}`}>{q.status}</span></td>
                  <td>
                    <div style={{ display:'flex',gap:5 }}>
                      <button className="btn-ghost" style={{ padding:'4px 8px',fontSize:'0.7rem' }} onClick={()=>downloadRecordPdf('quotation', q.id)}><Download size={11}/></button>
                      <button className="btn-outline" style={{ padding:'4px 8px',fontSize:'0.7rem' }} disabled={(q.approvals||0) < 2 || q.status === 'Sent'} onClick={()=>sendQuoteNow(q)}>Send</button>
                      {canCreate && <button className="btn-ghost" style={{ padding:'4px 8px',fontSize:'0.7rem' }} onClick={()=>{setEditQuo(q);setQuoForm({client:q.client,amount:String(q.amount),service:q.service,status:q.status});setShowQuoModal(true)}}><Edit2 size={11}/></button>}
                      {isFounder && <button className="btn-ghost" style={{ padding:'4px 8px',fontSize:'0.7rem',color:'var(--danger)' }} onClick={()=>deleteQuo(q.id)}><Trash2 size={11}/></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── INVOICE MODAL ── */}
      {showInvModal && (
        <div className="modal-overlay" onClick={()=>setShowInvModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div><p className="modal-title">{editInv?'Edit Invoice':'New Invoice'}</p><p className="modal-subtitle">Requires dual-founder approval</p></div>
              <button className="modal-close" onClick={()=>setShowInvModal(false)}><X size={18}/></button>
            </div>
            <div className="modal-form">
              <div className="form-group"><label className="form-label">Client *</label>
                <select className="form-input" value={invForm.client} onChange={e=>setInvForm({...invForm,client:e.target.value})}>
                  <option value="">Select client...</option>
                  {data.clients.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Amount (R) *</label><input className="form-input" type="number" value={invForm.amount} onChange={e=>setInvForm({...invForm,amount:e.target.value})} placeholder="0.00"/></div>
                <div className="form-group"><label className="form-label">Due Date</label><input className="form-input" type="date" value={invForm.due} onChange={e=>setInvForm({...invForm,due:e.target.value})}/></div>
              </div>
              <div className="form-group"><label className="form-label">Service Description</label><textarea className="form-input" rows={3} value={invForm.service} onChange={e=>setInvForm({...invForm,service:e.target.value})} placeholder="Services rendered..."/></div>
              {editInv && isFounder && (
                <div className="form-group"><label className="form-label">Status</label>
                  <select className="form-input" value={invForm.status} onChange={e=>setInvForm({...invForm,status:e.target.value})}>
                    {['Pending','Paid','Overdue'].map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
              )}
              <div className="info-banner info-banner-gold">⚡ This invoice requires approval from both founders before issuance.</div>
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={()=>setShowInvModal(false)}>Cancel</button>
              <button className="btn-gold" onClick={saveInvoice}>{editInv?'Save Changes':'Submit for Approval'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── QUOTATION MODAL ── */}
      {showQuoModal && (
        <div className="modal-overlay" onClick={()=>setShowQuoModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div><p className="modal-title">{editQuo?'Edit Quotation':'New Quotation'}</p><p className="modal-subtitle">Requires dual-founder approval</p></div>
              <button className="modal-close" onClick={()=>setShowQuoModal(false)}><X size={18}/></button>
            </div>
            <div className="modal-form">
              <div className="form-group"><label className="form-label">Client *</label>
                <select className="form-input" value={quoForm.client} onChange={e=>setQuoForm({...quoForm,client:e.target.value})}>
                  <option value="">Select client...</option>
                  {data.clients.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                  <option value="New Client">New Client</option>
                </select>
              </div>
              <div className="form-group"><label className="form-label">Amount (R) *</label><input className="form-input" type="number" value={quoForm.amount} onChange={e=>setQuoForm({...quoForm,amount:e.target.value})} placeholder="0.00"/></div>
              <div className="form-group"><label className="form-label">Service Description</label><textarea className="form-input" rows={3} value={quoForm.service} onChange={e=>setQuoForm({...quoForm,service:e.target.value})} placeholder="Services to be rendered..."/></div>
              <div className="info-banner info-banner-gold">⚡ This quotation requires approval from both founders before being sent to the client.</div>
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={()=>setShowQuoModal(false)}>Cancel</button>
              <button className="btn-gold" onClick={saveQuo}>{editQuo?'Save Changes':'Submit for Approval'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
